import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Body, Bold, Button, Tooltip, Link } from '@panwds/react-ui'
import { Input } from '@panwds/react-form'
import { HelpInPageIcon } from '@panwds/icons'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../../ErrorCodes'
import { isNil } from 'lodash'

import type { ChangeEvent } from 'react'

export type ILink = {
  text: string
  url: string
  external?: boolean
}

export interface ITextBoxProps {
  value?: string,
  onChangeHandler?: (value: string) => void,
  placeholder?: string;
  name: string;
  label: string;
  disabled?: boolean;
  showLabel?: boolean;
  tooltip?: string;
  subTitle?: string;
  height?: string;
  width?: string;
  defaultValue?: string;
  link?: ILink;
  errorMessage?: string;
  required?: boolean;
  validationHandler?: (value: string) => string | boolean
}

const UTextBox = (props: ITextBoxProps) => {
  const { trigger, formState, getValues, setError } = useFormContext()
  const { dirtyFields, errors } = formState
  const {
    placeholder,
    name,
    showLabel,
    label,
    validationHandler,
    subTitle,
    link,
    value,
    tooltip = '',
    disabled = false,
    height = '32px',
    width = '225px',
    defaultValue = '',
    errorMessage = '',
    required = false,
    onChangeHandler,
  } = props

  const onChangeValidator = useCallback((value) => {
    if (typeof validationHandler === 'function' && !validationHandler(value)) {
      setError(name, {})
      return false
    }
    return true
  }, [name, setError, validationHandler])

  const register = useMemo(() => {
    const register: Record<string, any> = {
      required,
      onChange: ((e: ChangeEvent<HTMLInputElement>) => {
        trigger(name)
        if (typeof onChangeHandler === 'function') {
          onChangeHandler(e.target.value)
        }
      }),
      validate: onChangeValidator
    }
    return register
  }, [name, onChangeHandler, onChangeValidator, required, trigger])
  return <>
    <div className='tw-flex tw-flex-row'>
      <Body size="sm" addClassName='tw-items-center tw-py-1.5 tw-pl-1'><Bold appearance="semibold">{label}</Bold></Body>
      {tooltip && <Tooltip label={tooltip}>
        <div className='tw-flex tw-items-center'>
          <Button
            size='sm'
            aria-label='contract-button'
            icon={<HelpInPageIcon size="sm" className="tw-text-icon-gray" />}
            appearance='clear'
            style={{ background: 'transparent' }}
          />
        </div>
      </Tooltip>}
    </div>
    <div className='tw-flex tw-flex-row'>
      <div className='cm-flex cm-flex-col'>
        <Input
          placeholder={placeholder}
          style={{ height: height, width: width }}
          name={name}
          label={showLabel ? label : ''}
          disabled={disabled}
          showLabel={showLabel}
          value={value}
          register={register}
        />
        {(dirtyFields[name]) && errors[name] &&
          <Body size="sm" appearance={'error'} addClassName='tw-items-center tw-py-1.5'>
            {ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[errorMessage]}
          </Body>
        }
      </div>
    </div>
    {subTitle?.length && <div className='tw-flex tw-flex-row tw-mt-1'>
      <Body size="sm" addClassName={'tw-pr-1'} appearance="secondary">{subTitle}</Body>
      {link && <Link size={'sm'} href={link.url} external={link.external}>
        {link.text}
      </Link>}
    </div>
    }
  </>

}
export default UTextBox
